import {getReasoningEffortLabel} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'
import {useAgentModels} from '@github-ui/copilot-shared-ui/agents/use-agent-models'

import {ModelIcon} from '@github-ui/one-copilot-composer/existing'
import type {ModelChangeMarker} from '../providers/ModelChangeContext'
import styles from './ModelChangeMarkerRow.module.css'

interface DisplayModel {
  id: string
  displayName: string
  logoURL?: string
}

/** Resolves the current model from the fetched list, falling back to a bare id so the row still renders while models load. */
function resolveModel(models: DisplayModel[] | undefined, modelId: string | undefined): DisplayModel {
  const match = modelId ? models?.find(model => model.id === modelId) : undefined
  return match ?? {id: modelId ?? '', displayName: modelId ?? 'model'}
}

/**
 * Inline timeline notice rendered when the user switches the model or reasoning
 * effort, matching the Copilot Desktop app: a vendor icon plus
 * "Model changed to …" / "Reasoning effort changed from … to …".
 */
export function ModelChangeMarkerRow({marker}: {marker: ModelChangeMarker}) {
  const {models} = useAgentModels()
  const model = resolveModel(models, marker.modelId)

  return (
    <div className={styles.row} data-testid="model-change-marker">
      <span className={styles.icon}>
        <ModelIcon model={model} />
      </span>
      <span className={styles.text}>
        {marker.kind === 'model' ? (
          <>
            Model changed to <span className={styles.emphasis}>{model.displayName}</span>
            {marker.reasoningEffort ? (
              <span className={styles.reasoning}> {getReasoningEffortLabel(marker.reasoningEffort)}</span>
            ) : null}
          </>
        ) : (
          <>
            Reasoning effort changed from{' '}
            <span className={styles.emphasis}>{getReasoningEffortLabel(marker.fromReasoningEffort)}</span> to{' '}
            <span className={styles.emphasis}>{getReasoningEffortLabel(marker.toReasoningEffort)}</span>
          </>
        )}
      </span>
    </div>
  )
}
